// Device-local opt-out for the pizza attention pop-ups.
//
// Unlike the user preferences that are synced to the backend via
// `updateUserPreferences`, this flag lives purely in localStorage and therefore
// only affects the current device/browser profile. When set, the globally
// mounted PizzaAttentionWatcher raises no pizza reminders on this device,
// regardless of the per-user `pizzaPopupNotifications` setting.

import { useCallback, useEffect, useState } from 'react';

export const NO_PIZZA_WARNINGS_KEY = 'noPizzaWarnings';

// Same-tab listeners are notified via this custom event; the native `storage`
// event only fires in *other* tabs, so we dispatch our own for the current one.
const CHANGE_EVENT = 'fz:noPizzaWarningsChange';

// Reads the device-local opt-out flag from localStorage.
export function getNoPizzaWarnings() {
  try {
    return window.localStorage.getItem(NO_PIZZA_WARNINGS_KEY) === 'true';
  } catch (_e) {
    // localStorage unavailable (e.g. private mode): treat as not suppressed.
    return false;
  }
}

// Persists the device-local opt-out flag and notifies listeners in this tab.
export function setNoPizzaWarnings(value) {
  const enabled = Boolean(value);
  try {
    if (enabled) {
      window.localStorage.setItem(NO_PIZZA_WARNINGS_KEY, 'true');
    } else {
      window.localStorage.removeItem(NO_PIZZA_WARNINGS_KEY);
    }
  } catch (_e) {
    // Ignore storage failures (e.g. private mode).
  }
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: enabled }));
  } catch (_e) {
    /* ignore */
  }
  return enabled;
}

// Reactive accessor: returns the current device-local opt-out flag and a setter,
// staying in sync with changes from other components (custom event) and other
// tabs (native storage event).
export function useNoPizzaWarnings() {
  const [enabled, setEnabled] = useState(getNoPizzaWarnings);

  useEffect(() => {
    const syncFromStorage = () => setEnabled(getNoPizzaWarnings());
    const onChange = event =>
      setEnabled(typeof event.detail === 'boolean' ? event.detail : getNoPizzaWarnings());
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', syncFromStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', syncFromStorage);
    };
  }, []);

  const update = useCallback(value => {
    setEnabled(setNoPizzaWarnings(value));
  }, []);

  return [enabled, update];
}
